import { useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { type AiModelResponse, adminUpdateModelMutation, errorMessage } from "@/api";
import { useAppForm } from "@/components/form/form";
import { AdvancedFields } from "./AdvancedFields";
import { DeprecationFields } from "./DeprecationFields";
import { EndpointFields } from "./EndpointFields";
import { ModelBasicsFields } from "./ModelBasicsFields";
import { modelFormSchema, modelToValues, toUpdateBody } from "./modelForm";
import { useRefreshModels } from "./useModelData";

/** Edit an existing model (PATCH). Disabling an in-use model asks to move its usage first. */
export function ConfigureModelForm({
  model,
  models,
  confirmDisable,
}: Readonly<{
  model: AiModelResponse;
  models: AiModelResponse[];
  confirmDisable: () => Promise<boolean>;
}>) {
  const { t } = useTranslation("adminModels");
  const navigate = useNavigate();
  const toast = useToast();
  const refresh = useRefreshModels();
  const update = useMutation(adminUpdateModelMutation());

  const form = useAppForm({
    defaultValues: modelToValues(model),
    validators: { onChange: modelFormSchema },
    onSubmit: async ({ value }) => {
      if (model.isEnabled && !value.isEnabled && !(await confirmDisable())) return;
      try {
        await update.mutateAsync({ path: { model_id: model.id }, body: toUpdateBody(value), throwOnError: true });
        await refresh();
        toast.success(t("form.saved", { name: value.name }));
        navigate("/admin/models");
      } catch (error) {
        toast.error(errorMessage(error, t("form.saveFailed")));
      }
    },
  });

  return (
    <form
      aria-label={t("form.editLabel")}
      className="flex flex-col gap-8"
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <ModelBasicsFields form={form} isNew={false} />
      <EndpointFields form={form} hasApiKey={model.hasApiKey ?? false} />
      <AdvancedFields form={form} />
      <DeprecationFields form={form} model={model} models={models} />
      <form.AppForm>
        <form.SubmitActions onCancel={() => navigate("/admin/models")} submitLabel={t("form.saveSubmit")} />
      </form.AppForm>
    </form>
  );
}
